import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import AuthCardFooter from "../../components/auth/AuthCardFooter";
import PasswordRequirements, {
  isPasswordValid,
} from "../../components/auth/PasswordRequirements";
import styles from "./AuthPage.module.css";

import {
  resetPassword as resetPasswordApi,
  classifyResetError,
} from "../../services/auth/passwordReset";
import { handleApiError } from "../../utils/handleApiError";

const schema = z
  .object({
    password: z.string().min(1, "Password is required"),
    password2: z.string().min(1, "Please confirm your password"),
  })
  .refine((v) => v.password === v.password2, {
    message: "Passwords do not match",
    path: ["password2"],
  })
  .refine((v) => isPasswordValid(v.password), {
    message: "Password does not meet the requirements",
    path: ["password"],
  });

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, token } = location.state || {};

  // Guard: if the user didn't come through /verify-otp we have no reset
  // token to reset against, so send them back to the start of the flow.
  useEffect(() => {
    if (!token) navigate("/recover-password", { replace: true });
  }, [token, navigate]);

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { password: "", password2: "" },
  });

  const password = watch("password") || "";

  const onSubmit = async (data) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      await resetPasswordApi({
        token,
        password: data.password,
        password2: data.password2,
      });
      navigate("/password-updated", { replace: true });
    } catch (err) {
      // A recognized reset-token problem (invalid / expired / used) routes to
      // the dedicated "start over" error screen; anything else (e.g. a
      // password field error) falls back to a toast.
      const kind = classifyResetError(err);
      if (kind) {
        navigate("/reset-code-error", {
          replace: true,
          state: { variant: kind, email },
        });
        return;
      }
      handleApiError(err);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className={styles.heading}>Create new password</h1>
      <p className={styles.subheading}>
        Choose a strong new password for your TOCA account.
      </p>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        noValidate
      >
        <div className={styles.field}>
          <label htmlFor="reset-pw" className={styles.label}>
            New Password
          </label>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
            <input
              id="reset-pw"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              className={styles.input}
              aria-invalid={!!errors.password}
              {...register("password")}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="reset-pw2" className={styles.label}>
            Confirm New Password
          </label>
          <div className={styles.inputWrap}>
            <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
            <input
              id="reset-pw2"
              type={showPw2 ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              className={styles.input}
              aria-invalid={!!errors.password2}
              aria-describedby={
                errors.password2 ? "reset-pw2-error" : undefined
              }
              {...register("password2")}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowPw2((s) => !s)}
              aria-label={showPw2 ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showPw2 ? faEyeSlash : faEye} />
            </button>
          </div>
          {errors.password2 && (
            <span
              id="reset-pw2-error"
              role="alert"
              className={styles.errorMsg}
            >
              {errors.password2.message}
            </span>
          )}
        </div>

        <PasswordRequirements password={password} />

        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={loading || !isPasswordValid(password)}
          aria-busy={loading}
        >
          {loading ? "Saving..." : "Update Password"}
        </button>
      </form>

      <AuthCardFooter />
    </>
  );
};

export default ResetPassword;
